import { Component, Inject, OnDestroy, OnInit, AfterViewInit, Injector, ViewChild } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Option } from "src/app/components/checklists/checklists";
import { Observable, Subscription } from "rxjs";
import { ChecklistTemplate, ChecklistExecStatus } from "../../../components/checklists/checklists";
import { ComponentType, FormType, ResourceType } from "src/app/common/enumerations/enumerations";
import { Schedule } from "src/app/components/schedules/models/schedule";
import { ScheduleType } from "src/app/components/schedules/models/schedule-type";
import { WFAction, WFComponent, WfSignature, WfTaskLocalResource } from "src/app/components/workflow/workflow";
import { ImageUploadService } from "src/app/services/file/image-upload.service";
import { Resource } from "src/app/components/catalogs/beamline-catalog/resource/resources";
import { Role } from "src/app/components/catalogs/roles/services/role";
import { YesNoDialogComponent } from "../../yes-no-dialog/yes-no-dialog.component";
import { BaseComponent } from "src/app/common/base/base.component";
import { ScheduleData, ScheduleObject } from "./cl-editor-schedule/cl-editor-schedule.component";
import { ChecklistBuilderPlaceholdersComponent } from "src/app/components/checklists/checklist-builder/checklist-builder-placeholders/checklist-builder-placeholders.component";
import { ClEditorTagInfoComponent, TagInfoData } from "./cl-editor-tag-info/cl-editor-tag-info.component";
import { BeamlineRequiringApprovalSummary } from "src/app/components/catalogs/bratoi/models/beamline-requiring-approval-summary";
import { ClEditorConditionsHelpComponent } from "./cl-editor-conditions-help/cl-editor-conditions-help.component";
import { ClEditorComponentHelpComponent } from "./cl-editor-component-help/cl-editor-component-help.component";
import { WorkFlowService } from "src/app/components/workflow/workflow.service";
import { PVData } from "../cl-image-upload/cl-image-upload-table/cl-image-upload-table-data/cl-image-upload-table-data.component";
import { InfoDialogComponent } from "../../info-dialog/info-dialog.component";

@Component({
  selector: 'cl-editor',
  templateUrl: './cl-editor.component.html',
  styleUrls: ['./cl-editor.component.scss'],
})
export class ClEditorComponent
  extends BaseComponent
  implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(ClEditorComponentHelpComponent)
  componentHelp!: ClEditorComponentHelpComponent;

  t1?: string | null;
  t2?: string | null;
  t3?: string | null;
  t4?: string;

  disabled: boolean;
  components!: WFComponent[];
  filteredComponents!: WFComponent[];
  control?: WFComponent;

  // typeCtrl = new FormControl();
  // roleCtrl = new FormControl();
  name?: string | null;
  condition?: string;
  sigCondition?: string;
  availableTags: string[] = [];
  code?: string;
  color!: string;
  scheduleResourceId?: number;
  scheduleTypeId?: number;
  locationName?: string | null;
  noOptions: boolean = false;

  type!: number;
  component?: WFComponent;

  error!: boolean;

  tmpOptions!: string;
  multiple!: boolean;
  tmpHeaders!: string;
  spotRows: any[] = [
    { id: 0, posx: 0, posy: 0, spotText: '', percX: 0, percY: 0 },
  ];

  options?: Option[] = [
    { value: 1, label: 'Yes' },
    { value: 0, label: 'No' },
  ];

  configuration?: string;

  approveAction?: boolean;
  unapproveAction?: boolean;
  disapproveAction?: boolean;
  amendmentAction?: boolean;
  cancelAction?: boolean;

  allRoles!: Role[];
  filteredRoles!: Role[];

  showHtml!: boolean;
  s: WfSignature;
  formType: FormType;
  originalRoles: Role[];

  conditions?: string[];

  loading!: boolean;
  serialNo: string;

  customButton: any;

  scheduleTypes$!: Observable<ScheduleType[]>;
  scheduleTypes!: ScheduleType[];
  scheduleType?: ScheduleType;
  schedules$!: Observable<Schedule[]>;
  schedules!: Schedule[];
  schedule!: Schedule;
  scheduleTypesSubs!: Subscription;
  schedulesSubs!: Subscription;
  clipboardMessage!: string;

  resources!: Resource[];
  resources$!: Observable<Resource[]>;
  resourcesSubs!: Subscription;

  components$!: Observable<WFComponent[]>;
  componentsSubs!: Subscription;

  componentType = ComponentType;

  roles: Role[];
  roles$!: Observable<Role[]>;
  rolesSubs!: Subscription;

  resourceTypes = ResourceType;

  formDirty = false;

  checklistTemplate: ChecklistTemplate;
  wfTableConfigID?: number;
  checklistBuilderMode = false;

  get checklistConditions(): string[] {
    const raw = this.checklistTemplate?.wfTable?.wfTableLocal?.wfSectionLocals
      ?.flatMap(s => s.wfStepLocals ?? [])
      ?.flatMap(step => step.wfTaskLocals ?? [])
      ?.map(task => task.code)
      ?.filter((c): c is string => !!c && c.trim().length > 0) ?? [];
    return [...new Set(raw)];
  }

  /**
   * Provides a flat task list for editor helpers that need checklist-wide context.
   */
  get checklistTasks(): WfTaskLocalResource[] {
    // Conditions help needs access to sibling task configuration, including multiple status options.
    return this.checklistTemplate?.wfTable?.wfTableLocal?.wfSectionLocals
      ?.flatMap(s => s.wfStepLocals ?? [])
      ?.flatMap(step => step.wfTaskLocals ?? []) ?? [];
  }

  isGlobalWorkflow = false;
  wfAction = WFAction;

  multi?: boolean;

  constructor(
    protected override injector: Injector,
    public dialogRef: MatDialogRef<ClEditorComponent>,
    private _image: ImageUploadService,
    private _workflow: WorkFlowService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    super(injector);
    this.s = this.utils.cloneDeep(data.s);
    this.formType = data.formType;
    this.roles = this.utils.cloneDeep(data.roles);
    this.serialNo = data.serialNo;
    this.originalRoles = data.roles ? this.utils.cloneDeep(data.roles) : null;
    this.checklistTemplate = data.checklistTemplate;
    this.disabled = data.disabled;
    this.wfTableConfigID = data.wfTableConfigID;
    this.isGlobalWorkflow = data.globalWorkflow;
    this.checklistBuilderMode = !!data.checklistBuilderMode;
  }

  get showScheduleLocationSelector(): boolean {
    return this.type == this.componentType.ScheduleAbsi ||
      (this.type == this.componentType.ScheduleAndCheckbox && !this.checklistBuilderMode);
  }

  async ngAfterViewInit(): Promise<void> {
    setTimeout(() => {
      const elem = document.getElementById('mat-dialog-0');
      if (elem) {
        elem.style.overflow = 'hidden';
      }
    }, 2000);
    if (this.formType) {
      const conditions = await this._workflow.getFormProperties(this.formType).toPromise();
      if (conditions) this.conditions = conditions;
    }
  }

  override ngOnDestroy(): void {
    this.scheduleTypesSubs?.unsubscribe();
    this.schedulesSubs?.unsubscribe();
    this.resourcesSubs?.unsubscribe();
    this.componentsSubs?.unsubscribe();
    this.rolesSubs?.unsubscribe();
  }

  ngOnInit() {
    // this.buildForm();
    this.loadRoles();
    this.loadResources();
    this.getScheduleTypes();

    this.components$ = this.store.select((state) => state.Components.data);
    this.componentsSubs = this.components$.subscribe((data) => {
      this.components = data
        .filter((x) => x.status === 1)
        .sort((a, b) => this.compare(a, b));
      this.filteredComponents = this.components;
      if (this.components.length) {
        this.approveAction = this.s?.approveAction;
        this.unapproveAction = this.s?.unapproveAction;
        this.disapproveAction = this.s?.disapproveAction;
        this.amendmentAction = this.s.amendmentAction;
        this.cancelAction = this.s.cancelAction;

        this.configuration = this.utils.cloneDeep(this.s?.configuration);
        this.options = this.s?.options;
        this.tmpOptions = JSON.stringify(this.s?.options);

        // Initialize component data from s after components are loaded
        this.initializeComponentData();
      }
    });
    this.utils.JSONparse(this.configuration)?.multiple;
  }

  /**
   * Initialize component properties from the stored signature object (s).
   * Must be called AFTER components are loaded to ensure proper component lookup.
   */
  private initializeComponentData(): void {
    if (this.s?.type != null) {
      this.t1 = this.s?.question;
      this.t2 = this.s?.question2;
      this.t3 = this.s?.question3;
      this.name = this.s?.name;
      this.condition = this.s?.condition;
      this.sigCondition = this.s?.sigCondition;
      this.code = this.s?.code;
      // Merge: master populates signature-condition tag options from the checklist being edited.
      this.loadAvailableTags();

      this.scheduleResourceId = this.s.scheduleResourceId;
      this.locationName = this.s.locationName;
      this.scheduleTypeId = this.s.scheduleTypeId;

      if (this.s.configuration) {
        const configuration = this.utils.JSONparse(this.s.configuration);
        if (configuration) {
          this.color = configuration.color ?? 'primary';
          this.scheduleTypeId =
            configuration.key === 'scheduleType'
              ? configuration.val
              : this.scheduleTypeId;

          const data = configuration.data;
          this._image.bookmarkedChannels.next([]);
          this.computeBookmarkedChannels(data);
        }
      }
      // Always re-resolve component from the loaded list to avoid stale serialized references
      // that the mat-autocomplete displayWith function cannot render correctly.
      const resolvedComponent = this.components.find((c) => c.id == this.s.type);
      if (resolvedComponent) {
        this.component = this.utils.cloneDeep(resolvedComponent);
        this.s.component = this.component;
      }
      this.type = this.s.type;
      this.componentType = ComponentType;

      if (this.usesQuestionTextField(this.type) && !this.t1?.trim() && this.name?.trim()) {
        this.t1 = this.name;
      }

      switch (this.s?.type) {
        case 8:
        case 9:
        case 24:
          this.t1 = this.s?.question2;
          this.t2 = this.s?.question;
          break;
      }
    }
  }

  reset() {
    this.name = '';
    this.t1 = '';
    this.t2 = '';
    this.t3 = '';
    this.t4 = '';
    this.roles = [];
    this.configuration = '';
    this.s.configuration = '';
    this.tmpOptions = '';
    this.options = [];
    this._image.bookmarkedChannels.next([]);
    // Cleared with the rest of the form: the field is hidden for components without signature
    // conditions, so a value left behind here would keep locking the task with nothing on screen
    // to explain why.
    this.sigCondition = undefined;
  }

  /**
   * Builds the tag choices offered for the signature condition, from the tags defined on the tasks
   * of this checklist template. Computed once when the editor opens rather than exposed as a
   * getter, so the option list is not rebuilt on every change detection pass.
   */
  private loadAvailableTags() {
    const tags = this.checklistTemplate?.wfTable?.wfTableLocal?.wfSectionLocals
      ?.flatMap(s => s.wfStepLocals ?? [])
      ?.flatMap(s => s.wfTaskLocals ?? [])
      ?.map(t => t.code?.trim())
      ?.filter((code): code is string => !!code) ?? [];

    // A stored value is kept in the list even if its task has since been retagged or removed, so
    // opening the editor never silently drops the condition.
    if (this.sigCondition?.trim()) tags.push(this.sigCondition.trim());

    this.availableTags = [...new Set(tags)].sort((a, b) => a.localeCompare(b));
  }

  /**
   * True for the tag belonging to the task being edited, which is offered but not selectable.
   */
  isOwnTag(tag: string) {
    return !!this.code?.trim() && tag.toUpperCase() == this.code!.trim().toUpperCase();
  }

  clearSigCondition() {
    this.sigCondition = undefined;
    this.formDirty = true;
  }

  loadRoles() {
    this.roles$ = this.store.select((state) => state.Roles.data);
    this.rolesSubs = this.roles$.subscribe((data) => {
      this.allRoles = data;
      this.filterRoles();
    });
  }

  loadResources() {
    this.resources$ = this.store.select((state) => state.Resources.data);
    this.resourcesSubs = this.resources$.subscribe((data) => {
      this.resources = data;
    });
  }

  filterRoles() {
    this.filteredRoles = this.allRoles.filter(
      (x) => !this.roles?.map((r) => r.id).includes(x.id),
    );
  }

  selectedRole(role: Role) {
    if (this.roles == null) {
      this.roles = [];
    }
    // const role: Role = event.option.value;
    // this.roles.push({ id: role.id, code: role.code });
    this.s.roleCodes = this.roles.map((r) => r.code).join('/');
    this.formDirty = true;
    this.filterRoles();
  }

  removeRole(role: Role) {
    // const index = this.roles.indexOf(role);
    // this.roles.splice(index, 1);
    this.s.roleCodes = this.roles.map((r) => r.code).join('/');
    this.formDirty = true;
  }

  compare(a: WFComponent, b: WFComponent) {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  }

  cancel() {
    if (this.formDirty) {
      const confirm = this.dialog.open(YesNoDialogComponent, {
        width: '400px',
        data: {
          message: this.getMessage(
            'Rad_Survey_Cancel_Boton_Component_Properties',
          ).description,
          icon: 'stop',
        },
      });
      confirm.afterClosed().subscribe((data) => {
        if (data) {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  async uploadImages(textValue?: string | null) {
    if (textValue) {
      const text = await this._image.uploadImagesToServer(
        textValue,
        '/' +
        (FormType[this.formType] ?? 'Generic') +
        '/' +
        this.serialNo +
        '/'
      );
      this.formDirty = true;
      return text;
    }
    return '';
  }

  async accept() {
    console.log(this.component);
    const text1 = this.usesQuestionTextField(this.type) && !this.t1?.trim()
      ? this.name
      : this.t1;
    this.s.question = this.clean(await this.uploadImages(text1));
    this.s.question2 = this.clean(await this.uploadImages(this.t2));
    this.s.question3 = this.clean(await this.uploadImages(this.t3));
    this.s.options = this.options;
    this.s.name = this.name;
    this.s.condition = this.condition;
    // Never persisted for a component that cannot show the field, so switching a task to another
    // component type cannot leave an invisible condition disabling it at runtime.
    this.s.sigCondition = this.component?.hasSigConditions ? this.sigCondition : undefined;
    this.s.code = this.code;

    this.s.approveAction = this.approveAction;
    this.s.unapproveAction = this.unapproveAction;
    this.s.disapproveAction = this.disapproveAction;
    this.s.amendmentAction = this.amendmentAction;
    this.s.cancelAction = this.cancelAction;

    this.s.scheduleTypeId = this.scheduleTypeId;
    this.s.scheduleResourceId = this.scheduleResourceId;
    this.s.locationName = this.locationName;

    console.log(this.configuration);
    this.s.configuration = this.configuration;
    this.s.component = this.utils.cloneDeep(this.component);
    this.s.type = this.type;

    switch (this.s.type) {
      case 8:
      case 9:
      case 24:
        this.s.question2 = this.clean(await this.uploadImages(this.t1));
        this.s.question = this.clean(await this.uploadImages(this.t2));
        break;
    }
    this.data.s = this.s;
    this.data.s.roles = this.roles.map((r) => r.id);
    this.data.roles = this.roles;
    this.data.originalRoles = this.originalRoles;
    this.dialogRef.close(this.data);
  }

  clean(t?: string | null) {
    if (t) {
      t = t
        ?.replace('<p>', '')
        .replace('</p>', '<br>')
        .replace('<strong>', '<b>')
        .replace('</strong>', '</b>');
      if (t.includes('<br>') && t.lastIndexOf('<br>') == t.length - 4) {
        t = t.slice(0, -4);
      }
    }
    return t;
  }

  usesQuestionTextField(type?: number): boolean {
    return type == ComponentType.CheckboxSignature ||
      type == ComponentType.ButtonSignature ||
      type == ComponentType.ButtonSignatureDuplicate ||
      type == ComponentType.Comments ||
      type == ComponentType.CloseButton;
  }

  html() {
    this.showHtml = !this.showHtml;
  }

  jsonOptions(o: string) {
    try {
      return JSON.parse(o);
    } catch (err) {
      return [];
    }
  }

  optionsChange(e: any) {
    this.tmpOptions = e;
    this.options = this.jsonOptions(this.tmpOptions);
    this.formDirty = true;
  }

  checkboxChanged(e: any) {
    this.configuration = JSON.stringify(e);
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  displayType(component: { name: any } | null): string {
    return component ? component.name : '';
  }

  selectedType(e: { option: { value: WFComponent } }) {
    const value = e.option.value as WFComponent;
    this.type = value.id;
    this.component = this.components.find((c) => c.id == value.id);
    this.reset();
    this.code =
      this.code && !this.code?.includes('RMON') ? this.code : value.defaultTag;

    if (this.type == 7) this.name = 'Note:';
    this.formDirty = true;
  }

  scheduleChanged(e: ScheduleObject) {
    this.scheduleTypeId = e?.scheduleType?.id;
    this.scheduleResourceId = e?.scheduleData?.resourceId;
    this.locationName = e?.scheduleData?.name;
    this.configuration = JSON.stringify({ scheduleType: e.scheduleType?.id, resourdeId: this.scheduleResourceId });
    this.formDirty = true;
  }

  locationChanged(location: Resource) {
    this.s.locationName = location.name;
    this.configuration = JSON.stringify({ key: 'location', val: location.id });
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  shieldingSettingsChanged(e: any) {
    this.configuration = JSON.stringify(e);
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  configurationChanged(e: any) {
    this.configuration = e;
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  setOptions(options: Option[]) {
    this.tmpOptions = JSON.stringify(options);
    this.options = this.jsonOptions(this.tmpOptions);
    this.formDirty = true;
  }

  setCondition(condition: string) {
    this.condition = condition;
    this.formDirty = true;
  }

  colorChanged(color: string) {
    let configuration = this.utils.JSONparse(this.configuration);
    if (!configuration) {
      configuration = {};
    }
    configuration.color = color;
    this.color = color;
    this.configuration = this.utils.JSONstringify(configuration);
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  filterRoles2(e: any) {
    const text = e.target.value;
    this.filterRoles();
    this.filteredRoles = this.filteredRoles.filter(
      (x) =>
        x.name?.toLowerCase().includes(text.toLowerCase()) ||
        (x.code?.toLowerCase().includes(text.toLowerCase()) &&
          !this.roles?.map((c) => c.id).includes(x.id)),
    );
  }

  filterControls(e: any) {
    const value = e.target.value;
    this.filteredComponents = this.components.filter((x) =>
      x.name.toLowerCase().includes(value.toLowerCase()),
    );
  }

  async getScheduleTypes() {
    this.scheduleTypes$ = this.store.select(
      (state) => state.ScheduleTypes.data,
    );
    this.scheduleTypesSubs = this.scheduleTypes$.subscribe(async (data) => {
      this.scheduleTypes = data;
      this.scheduleType = data.find((x) => x.id == this.scheduleTypeId);
      this.scheduleTypeId = this.scheduleType?.id;
      await this.getSchedules();
    });
  }

  async getSchedules() {
    this.schedules$ = this.store.select((state) => state.Schedules.data);
    this.schedulesSubs = this.schedules$.subscribe((data) => {
      const scheduleDatas: ScheduleData[] = [];
      data.map((schedule) => {
        if (
          schedule.statusId != 2 &&
          schedule.typeId == this.scheduleType?.id
        ) {
          schedule.scheduleResources.map((resource) => {
            const scheduleData: ScheduleData = {
              scheduleResourcesId: resource.id,
              scheduleId: schedule.id,
              name: resource.resourceId
                ? resource.resource?.name
                : resource.resourceString,
              resourceId: resource.resourceId ? resource.resourceId : 0,
              dateValue1: schedule.lastCompleted,
              dateValue2: schedule.nextDue,
              dateValue3: schedule.extendedDate,
            };
            scheduleDatas.push(scheduleData);
          });
          // this.scheduleResourceId = this.schedule.id;
        }
      });
    });
  }

  headersChange(e: string) {
    if (!this.configuration) {
      this.configuration = e;
    }
    const configuration = this.utils.cloneDeep(JSON.parse(this.configuration));
    const newConfig = JSON.parse(e);
    configuration.headers = newConfig.headers;
    configuration.data = newConfig.data;
    configuration.columnsBuilder = newConfig.columnsBuilder;
    configuration.columnsChecklist = newConfig.columnsChecklist;
    configuration.columns = newConfig.columns;
    configuration.widths = newConfig.widths;
    configuration.spots = newConfig.spots;
    configuration.colorize = newConfig.colorize;
    configuration.includeIM = newConfig.includeIM;
    configuration.includeIMPos = newConfig.includeIMPos;
    configuration.images = newConfig.images;
    configuration.channels = newConfig.channels;
    configuration.channelsData = newConfig.channelsData;
    this.configuration = JSON.stringify(configuration);
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  change(e: string) {
    let configuration: any;
    if (!this.configuration) {
      configuration = JSON.parse(e);
    } else {
      configuration = JSON.parse(this.configuration);
      const newConfiguration = JSON.parse(e);
      // If columnsBuilder is present, this is a full RADSurveyImageTable save from the popup — merge all its fields
      if (newConfiguration.columnsBuilder !== undefined) {
        configuration.columnsBuilder = newConfiguration.columnsBuilder;
        configuration.columnsChecklist = newConfiguration.columnsChecklist;
        configuration.colorize = newConfiguration.colorize;
        configuration.includeIM = newConfiguration.includeIM;
        configuration.includeIMPos = newConfiguration.includeIMPos;
        configuration.channels = newConfiguration.channels;
      }
      const data = newConfiguration.data;
      const spots = newConfiguration.spots;
      const widths = newConfiguration.widths;
      const images = newConfiguration.images;
      if (data) configuration.data = data;
      if (spots) configuration.spots = spots;
      if (widths) configuration.widths = widths;
      if (images) configuration.images = images;
      this.computeBookmarkedChannels(data);
    }
    this.configuration = this.utils.cloneDeep(JSON.stringify(configuration));
    if (this.s.configuration != this.configuration) {
      this.s.configuration = this.configuration;
      this.formDirty = true;
    }

  }

  bookmarkedChannels?: PVData[] = [];

  computeBookmarkedChannels(rows: Array<{ data?: unknown }>): void {
    if (this.s.component?.id !== ComponentType.RADSurveyImageTable) {
      this.bookmarkedChannels = [];
      this._image.bookmarkedChannels.next([]);
      return;
    }

    const key = (n?: string) => n?.trim().toLowerCase();
    const lastSeen = new Map<string, PVData>();   // preserve a PVData to emit
    const count = new Map<string, number>();  // how many rows bookmark this channel

    for (const r of rows ?? []) {
      const arr = this.utils.JSONparse(r?.data as string);
      if (!Array.isArray(arr)) continue;

      for (const ch of arr as PVData[]) {
        const k = key(ch?.name);
        if (!k || lastSeen.get(k)) continue;
        lastSeen.set(k, ch);
        if (ch.bookmarked) count.set(k, (count.get(k) ?? 0) + 1);
      }
    }

    // Keep only channels with count > 0 (i.e., still bookmarked somewhere)
    const next = [...count.entries()]
      .filter(([, n]) => n > 0)
      .map(([k]) => lastSeen.get(k)!)
      .sort((a, b) => a.name.localeCompare(b.name)); // optional

    this.bookmarkedChannels = next;
    this._image.bookmarkedChannels.next([...next]);
  }

  statusSelected(configuration: any) {
    if (!configuration) {
      configuration = {};
    }
    this.configuration = this.utils.cloneDeep(
      this.utils.JSONstringify(configuration),
    );
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  actionStatusSelected(status: ChecklistExecStatus, action: WFAction) {
    let configuration = this.utils.JSONparse(this.configuration);
    if (!configuration) {
      configuration = {};
    }
    switch (action) {
      case WFAction.Approve:
        configuration.approveNextStatus = status;
        break;
      case WFAction.Unapprove:
        configuration.unapproveNextStatus = status;
        break;
      case WFAction.Disapprove:
        configuration.disapproveNextStatus = status;
        break;
      case WFAction.Amendment:
        configuration.amendmentNextStatus = status;
        break;
      case WFAction.Cancel:
        configuration.cancelNextStatus = status;
        break;
      case WFAction.Close:
        configuration.closeNextStatus = status;
        break;
    }

    this.configuration = this.utils.cloneDeep(
      this.utils.JSONstringify(configuration),
    );
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  actionNextStepSelected(
    stepID: number | undefined = undefined,
    action: number,
  ) {
    switch (action) {
      case WFAction.Approve:
        this.s.approveNextStepConfigID = stepID;
        break;
      case WFAction.Unapprove:
        this.s.unapproveNextStepConfigID = stepID;
        break;
      case WFAction.Disapprove:
        this.s.disapproveNextStepConfigID = stepID;
        break;
      case WFAction.Amendment:
        this.s.amendmentNextStepConfigID = stepID;
        break;
      case WFAction.Cancel:
        this.s.cancelNextStepConfigID = stepID;
        break;
      case WFAction.Close:
        break;
    }
    this.formDirty = true;
  }

  // BRATOIChanged(blRequiring: BeamlineRequiringApprovalSummary) {
  //   const configuration = blRequiring;
  //   this.configuration = JSON.stringify(configuration);
  //   this.s.configuration = this.configuration;
  //   this.formDirty = true;
  // }

  setRoleByCode(roleCode: string) {
    if (this.allRoles?.length) {
      const role = this.allRoles.find((x) => x.code === roleCode);
      if (role?.id && !this.roles.find((x) => x.id === role?.id)) {
        this.roles.push({ id: role?.id, code: role?.code } as Role);
        this.filterRoles();
      }
    }
  }

  placeholders() {
    if (this.checklistTemplate && !this.checklistTemplate?.resource) {
      this.checklistTemplate.resource = this.resources.find(
        (x) => x.id == this.checklistTemplate.resourceID,
      );
    }
    const settings = this.dialog.open(ChecklistBuilderPlaceholdersComponent, {
      minHeight: '500px',
      data: { resource: this.checklistTemplate?.resource },
      disableClose: true,
      autoFocus: false,
    });
    settings.afterClosed().subscribe((data) => {
      this.clipboardMessage =
        'Placeholder <b>' + data + '</b> copied to cliboard';
      setTimeout(() => {
        const placeholderMessage =
          document.getElementById('placeholderMessage');
        if (placeholderMessage) {
          placeholderMessage.style.opacity = '1';
          const timerId = setInterval(() => {
            // start interval loop
            const opacity = placeholderMessage.style.opacity; // get current opacity
            if (+opacity == 0) {
              // check if its 0 yet
              clearInterval(timerId); // if so, exit from interval loop
            } else {
              placeholderMessage.style.opacity = (+opacity - 0.05).toString(); // else remove 0.05 from opacity
            }
          }, 100); // run every 0.1 second
        }
      }, 5000);
    });
  }

  placeHolderSelected(placeholder: string) {
    let configuration = this.utils.JSONparse(this.configuration);
    if (!configuration) {
      configuration = {};
    }
    configuration.placeholder = placeholder;
    this.configuration = this.utils.JSONstringify(configuration);
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  radMonitorConfigChanged(radMonitorConfig: any) {
    this.configuration = JSON.stringify(radMonitorConfig);
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  radMeterConfigChanged() {
    this.s.configuration = this.configuration;
    this.formDirty = true;
  }

  textChanged(e: string, number: any) {
    switch (number) {
      case 1:
        this.t1 = e;
        break;
      case 2:
        this.t2 = e;
        break;
      case 3:
        this.t3 = e;
        break;
      case 4:
        this.t4 = e;
        break;
    }
    this.formDirty = true;
  }

  actionChanged() {
    this.formDirty = true;
  }

  showTagInfo() {
    const tagBox = document.getElementById('tagBox')?.getBoundingClientRect();
    if (tagBox) {
      const dialog = this.dialog.open(ClEditorTagInfoComponent, {
        data: {
          component: this.component,
          tag: this.code,
          multi: this.utils.JSONparse(this.configuration)?.multiple,
        } as TagInfoData,
        disableClose: false,
        autoFocus: false,
        width: '600px',
        hasBackdrop: false,
        position: {
          left: tagBox.right - 600 + 'px',
          top: tagBox.top + 50 + 'px',
        },
      });
    }
  }

  showConditionsHelp() {
    const box = document
      .getElementById('conditionsBox')
      ?.getBoundingClientRect();
    if (box) {
      const dialog = this.dialog.open(ClEditorConditionsHelpComponent, {
        data: {
          component: this.component,
          conditions: this.conditions,
          formType: this.formType,
          tag: this.code,
          multi: this.utils.JSONparse(this.configuration)?.multiple,
        } as TagInfoData,
        disableClose: false,
        autoFocus: false,
        width: '600px',
        hasBackdrop: false,
        position: { left: box.right - 600 + 'px', top: box.top + 50 + 'px' },
      });
    }
  }

  checkAcceptDisabled() {
    if (!this.component) {
      return true;
    }
    if (this.error) {
      return true;
    }
    if (
      this.component?.needRoles &&
      !this.component.hasMultipleRoles &&
      !this.roles?.length
    ) {
      return true;
    }
    return false;
  }

  infoPopup(sigCondition?: string, tag?: string) {
    const s = sigCondition?.trim() || 'A';
    const t = tag?.trim() || 'B';
    this.dialog.open(InfoDialogComponent, {
      width: '45vw',
      data: {
        title: 'Signature Condition Info',
        subTitle: 'A signature condition stops one person from signing both of two tasks, so that a second person has to sign the other one.',
        message: this.formatSigCondition(s, t),
        footer: ''
      }
    });
  }

  formatSigCondition(s: string, t: string): string {
    return `
        <p>
          Pick the <b>Tag</b> of the task that must not be signed by the same person as this one.
          Only tags already defined in this checklist can be selected, and this task's own tag
          (<b>${t}</b>) is greyed out because a task cannot exclude itself.
        </p>
        <p>
          <b>Set the condition on both tasks.</b> A signature condition only blocks the task it is
          set on, so pointing the two tasks at each other is what makes the pair mutually exclusive:
        </p>
        <p>
          <b>'Checkbox Question 1'</b>
          <ul>
            <li><b>Tag:</b> ${t}</li>
            <li><b>Signature Condition:</b> ${s}</li>
          </ul>
        </p>
        <p>
          <b>'Checkbox Question 2'</b>
          <ul>
            <li><b>Tag:</b> ${s}</li>
            <li><b>Signature Condition:</b> ${t}</li>
          </ul>
        </p>
        <p>
          Whoever signs one of these two tasks can no longer sign the other: it is greyed out for
          them, and the server refuses the signature if it is attempted anyway. The checklist
          builder reports an error when a signature condition points at a tag that does not exist,
          at the task's own tag, or at a task that does not point back.
        </p>
      `;
  }
}
